import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import urlPatterns from "../../../urls";
import { I18N_NAMESPACES } from "../../../helpers/constants";
import { getCashAmountLabel } from "../../../helpers/i18n";
import { BuildYourOwnProgress as Progress } from "../../../helpers/buildYourOwn";
import fallbackBoxImage from "../../../assets/images/temp/checkout-item-wine-box.png";
import closeIcon from "../../../assets/images/icons/cross.svg";
import BuildYourOwnProgressMeter from "./Meter";
import { Actions, Body, CheckoutLink, CloseX, Note, Thumb, Title, Wrapper } from "./style";

interface BuildYourOwnProgressProps {
  progress: Progress;
  /** Renders the close X, top right; the caller confirms before removing. */
  onRemove?: () => void;
  isRemoving?: boolean;
}

/** A box is in the bag and nothing started is holding the order back. */
const canCheckout = (progress: Progress) =>
  progress.grantedQuantity > 0 && !progress.blocksCheckout && !progress.checkoutError;

/**
 * The shop-page banner: boxes unlocked so far and "X of N wines" towards the
 * next, with the backend's counts (or a guest's local preview) already resolved
 * by the caller. Whatever the progress, a note underneath says the calendar is
 * in the one-off cart and what that order takes (the box minimum too, when the
 * code has one). Once a calendar is fully redeemed it also offers the way to
 * checkout, to the right (below the copy on phones). The cart renders the same
 * meter inside its own line items.
 */
const BuildYourOwnProgress = ({ progress, onRemove, isRemoving = false }: BuildYourOwnProgressProps) => {
  const { t } = useTranslation([I18N_NAMESPACES.COMMON]);
  const { perNumberOfWines, perWineSpend } = progress;

  return (
    <Wrapper data-testid="build-your-own-progress">
      {onRemove && (
        <CloseX
          type="button"
          onClick={onRemove}
          disabled={isRemoving}
          aria-label={t("common:buildYourOwn.removeCalendar")}
        >
          <img src={closeIcon} alt="" aria-hidden="true" />
        </CloseX>
      )}
      <Thumb src={progress.freebieImage ?? fallbackBoxImage} alt="" />
      <Body>
        <Title>{t("common:buildYourOwn.title")}</Title>
        <BuildYourOwnProgressMeter progress={progress} celebrate />
        <Note data-testid="build-your-own-note">
          {perWineSpend && perWineSpend > 0
            ? t("common:buildYourOwn.addedToCartNoteWithSpend", {
                perNumberOfWines,
                amount: getCashAmountLabel(perWineSpend),
              })
            : t("common:buildYourOwn.addedToCartNote", { perNumberOfWines })}
        </Note>
      </Body>
      {canCheckout(progress) && (
        <Actions>
          <CheckoutLink as={Link} to={urlPatterns.CHECKOUT} data-testid="build-your-own-checkout">
            {t("common:checkout")}
          </CheckoutLink>
        </Actions>
      )}
    </Wrapper>
  );
};

export default BuildYourOwnProgress;
